/**
 * Media Library Component
 * Full-featured media asset management with folders, uploads, translations, and sharing.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Folder,
  FolderPlus,
  Upload,
  File,
  Image,
  Video,
  Music,
  Link as LinkIcon,
  MoreVertical,
  Search,
  Grid,
  List,
  ChevronRight,
  Home,
  Trash2,
  Edit3,
  Copy,
  Share2,
  Globe,
  Tag,
  Filter,
  X,
  Check,
  Plus,
  Download,
  Eye,
  BarChart2,
  Languages,
  RefreshCw
} from 'lucide-react';
import { getWeaveinApiUrl } from '@/src/config';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import { RegulatedOnly } from '@/components/compliance/RegulatedOnly';
import { useComplianceMode } from '@/hooks/useComplianceMode';
import { approvalsAPI } from '@/services/api/approvals';

/** Build auth headers with the active CBO id so org switches actually scope queries.
 *  Without this, the backend falls through to the JWT default org and leaks data. */
function buildAuthHeaders(extra?: Record<string, string>): Record<string, string> {
  const token = localStorage.getItem('auth_token');
  const cboId = localStorage.getItem('active_cbo_id') || '';
  const out: Record<string, string> = { ...extra };
  if (token) out['Authorization'] = `Bearer ${token}`;
  if (cboId) out['X-CBO-ID'] = cboId;
  return out;
}

type ComplianceStatus = 'active' | 'expiring_soon' | 'expired' | 'pending_approval' | 'retired';

const COMPLIANCE_STATUS_BADGE: Record<ComplianceStatus, string> = {
  active: 'bg-green-100 text-green-900',
  expiring_soon: 'bg-amber-100 text-amber-900',
  expired: 'bg-red-100 text-red-900',
  pending_approval: 'bg-blue-100 text-blue-900',
  retired: 'bg-gray-200 text-gray-700',
};

const COMPLIANCE_STATUS_LABEL: Record<ComplianceStatus, string> = {
  active: 'Active',
  expiring_soon: 'Expiring Soon',
  expired: 'Expired',
  pending_approval: 'Pending Approval',
  retired: 'Retired',
};

// Types
interface MediaFolder {
  id: number;
  name: string;
  description?: string;
  path: string;
  is_shared: boolean;
  subfolder_count: number;
  asset_count: number;
  created_at: string;
}

interface MediaAsset {
  id: number;
  uuid: string;
  name: string;
  description?: string;
  asset_type: string;
  mime_type?: string;
  file_size?: number;
  external_url?: string;
  thumbnail_url?: string;
  default_language: string;
  tags: string[];
  is_public: boolean;
  is_shared_org: boolean;
  created_at: string;
  folder_name?: string;
  link_count: number;
  translation_count: number;
  effective_date?: string | null;
  expiration_date?: string | null;
  expiration_policy?: 'never' | 'always' | 'sometimes' | null;
  allow_use_after_expiry?: boolean | null;
  compliance_status?: ComplianceStatus | null;
  regulated_category?: string | null;
}

interface MediaTag {
  id: number;
  name: string;
  color?: string;
  usage_count: number;
}

interface SharingLink {
  id: number;
  short_code: string;
  full_url: string;
  link_type: string;
  is_public: boolean;
  click_count: number;
  unique_clicks: number;
  expires_at?: string;
  created_at: string;
}

const ASSET_TYPE_ICONS: Record<string, React.ElementType> = {
  document: File,
  image: Image,
  video: Video,
  audio: Music,
  web_link: LinkIcon,
  artifact: File
};

const ASSET_TYPE_COLORS: Record<string, string> = {
  document: 'text-blue-500 bg-blue-100 dark:bg-blue-900/30',
  image: 'text-green-500 bg-green-100 dark:bg-green-900/30',
  video: 'text-purple-500 bg-purple-100 dark:bg-purple-900/30',
  audio: 'text-orange-500 bg-orange-100 dark:bg-orange-900/30',
  web_link: 'text-cyan-500 bg-cyan-100 dark:bg-cyan-900/30',
  artifact: 'text-gray-500 bg-gray-100 dark:bg-gray-800'
};

export function MediaLibrary() {
  const { fmt } = useFormattedDate();
  // State
  const [folders, setFolders] = useState<MediaFolder[]>([]);
  const [assets, setAssets] = useState<MediaAsset[]>([]);
  const [tags, setTags] = useState<MediaTag[]>([]);
  const [currentFolder, setCurrentFolder] = useState<number | null>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<Array<{ id: number | null; name: string }>>([
    { id: null, name: 'Media Library' }
  ]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedAsset, setSelectedAsset] = useState<MediaAsset | null>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [showAssetDetail, setShowAssetDetail] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [totalAssets, setTotalAssets] = useState(0);

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = buildAuthHeaders();

      // Fetch folders
      const foldersUrl = currentFolder
        ? `${getWeaveinApiUrl()}/api/media/folders?parent_folder_id=${currentFolder}`
        : `${getWeaveinApiUrl()}/api/media/folders`;
      const foldersRes = await fetch(foldersUrl, { headers });
      if (foldersRes.ok) {
        setFolders(await foldersRes.json());
      }

      // Build assets URL with filters
      const params = new URLSearchParams();
      if (currentFolder) params.append('folder_id', currentFolder.toString());
      if (selectedType) params.append('asset_type', selectedType);
      if (searchQuery) params.append('search', searchQuery);
      if (selectedTags.length) params.append('tags', selectedTags.join(','));

      const assetsRes = await fetch(
        `${getWeaveinApiUrl()}/api/media/assets?${params.toString()}`,
        { headers }
      );
      if (assetsRes.ok) {
        const data = await assetsRes.json();
        setAssets(data.assets);
        setTotalAssets(data.total);
      }

      // Fetch tags
      const tagsRes = await fetch(`${getWeaveinApiUrl()}/api/media/tags`, { headers });
      if (tagsRes.ok) {
        setTags(await tagsRes.json());
      }
    } catch (err) {
      console.error('Error fetching media library:', err);
    } finally {
      setLoading(false);
    }
  }, [currentFolder, selectedType, searchQuery, selectedTags]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Navigation
  const navigateToFolder = (folder: MediaFolder) => {
    setCurrentFolder(folder.id);
    setBreadcrumbs([...breadcrumbs, { id: folder.id, name: folder.name }]);
  };

  const navigateToBreadcrumb = (index: number) => {
    const crumb = breadcrumbs[index];
    setCurrentFolder(crumb.id);
    setBreadcrumbs(breadcrumbs.slice(0, index + 1));
  };

  // Format file size
  const formatFileSize = (bytes?: number): string => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  };

  // Render folder card
  const renderFolderCard = (folder: MediaFolder) => (
    <div
      key={`folder-${folder.id}`}
      className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 cursor-pointer hover:border-blue-500 dark:hover:border-blue-400 transition-colors"
      onClick={() => navigateToFolder(folder)}
    >
      <div className="flex items-center space-x-3">
        <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
          <Folder className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-gray-900 dark:text-white truncate">{folder.name}</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {folder.subfolder_count} folders, {folder.asset_count} files
          </p>
        </div>
        <ChevronRight className="w-5 h-5 text-gray-400" />
      </div>
    </div>
  );

  // Render asset card (grid view)
  const renderAssetCard = (asset: MediaAsset) => {
    const TypeIcon = ASSET_TYPE_ICONS[asset.asset_type] || File;
    const colorClass = ASSET_TYPE_COLORS[asset.asset_type] || ASSET_TYPE_COLORS.artifact;

    return (
      <div
        key={`asset-${asset.id}`}
        className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden hover:border-blue-500 dark:hover:border-blue-400 transition-colors cursor-pointer"
        onClick={() => {
          setSelectedAsset(asset);
          setShowAssetDetail(true);
        }}
      >
        {/* Thumbnail or Icon */}
        <div className="aspect-video bg-gray-100 dark:bg-gray-700 flex items-center justify-center relative">
          {asset.thumbnail_url ? (
            <img src={asset.thumbnail_url} alt={asset.name} className="w-full h-full object-cover" />
          ) : (
            <div className={`p-4 rounded-full ${colorClass}`}>
              <TypeIcon className="w-8 h-8" />
            </div>
          )}
          {asset.compliance_status && (
            <span
              className={`absolute top-2 right-2 px-2 py-0.5 text-[10px] font-semibold rounded-full ${
                COMPLIANCE_STATUS_BADGE[asset.compliance_status as ComplianceStatus] ?? COMPLIANCE_STATUS_BADGE.active
              }`}
            >
              {COMPLIANCE_STATUS_LABEL[asset.compliance_status as ComplianceStatus] ?? asset.compliance_status}
            </span>
          )}
        </div>

        {/* Info */}
        <div className="p-3">
          <h3 className="font-medium text-gray-900 dark:text-white truncate" title={asset.name}>
            {asset.name}
          </h3>
          <div className="flex items-center justify-between mt-2">
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {formatFileSize(asset.file_size)}
            </span>
            <div className="flex items-center space-x-2">
              {asset.translation_count > 0 && (
                <span className="flex items-center text-xs text-blue-500">
                  <Languages className="w-3 h-3 mr-1" />
                  {asset.translation_count}
                </span>
              )}
              {asset.link_count > 0 && (
                <span className="flex items-center text-xs text-green-500">
                  <LinkIcon className="w-3 h-3 mr-1" />
                  {asset.link_count}
                </span>
              )}
              {asset.is_public && (
                <Globe className="w-3 h-3 text-cyan-500" />
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render asset row (list view)
  const renderAssetRow = (asset: MediaAsset) => {
    const TypeIcon = ASSET_TYPE_ICONS[asset.asset_type] || File;
    const colorClass = ASSET_TYPE_COLORS[asset.asset_type] || ASSET_TYPE_COLORS.artifact;

    return (
      <tr
        key={`asset-${asset.id}`}
        className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
        onClick={() => {
          setSelectedAsset(asset);
          setShowAssetDetail(true);
        }}
      >
        <td className="px-4 py-3">
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded ${colorClass}`}>
              <TypeIcon className="w-4 h-4" />
            </div>
            <div>
              <div className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                <span>{asset.name}</span>
                {asset.compliance_status && (
                  <span
                    className={`px-2 py-0.5 text-[10px] font-semibold rounded-full ${
                      COMPLIANCE_STATUS_BADGE[asset.compliance_status as ComplianceStatus] ?? COMPLIANCE_STATUS_BADGE.active
                    }`}
                  >
                    {COMPLIANCE_STATUS_LABEL[asset.compliance_status as ComplianceStatus] ?? asset.compliance_status}
                  </span>
                )}
              </div>
              {asset.description && (
                <div className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-md">
                  {asset.description}
                </div>
              )}
            </div>
          </div>
        </td>
        <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
          {asset.asset_type}
        </td>
        <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
          {formatFileSize(asset.file_size)}
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center space-x-2">
            {asset.translation_count > 0 && (
              <span className="inline-flex items-center px-2 py-0.5 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded">
                <Languages className="w-3 h-3 mr-1" />
                {asset.translation_count}
              </span>
            )}
            {asset.link_count > 0 && (
              <span className="inline-flex items-center px-2 py-0.5 text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded">
                <LinkIcon className="w-3 h-3 mr-1" />
                {asset.link_count}
              </span>
            )}
          </div>
        </td>
        <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
          {fmt.short(asset.created_at)}
        </td>
        <td className="px-4 py-3">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setSelectedAsset(asset);
              setShowShareModal(true);
            }}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <Share2 className="w-4 h-4" />
          </button>
        </td>
      </tr>
    );
  };

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-end gap-2 p-4 border-b border-v2border">
        <button
          onClick={() => setShowFolderModal(true)}
          className="px-3 py-2 border border-v2border bg-surface text-ink hover:bg-paper-2 rounded-lg flex items-center space-x-2"
        >
          <FolderPlus className="w-4 h-4" />
          <span>New Folder</span>
        </button>
        <button
          onClick={() => setShowUploadModal(true)}
          className="px-3 py-2 bg-v2accent hover:bg-v2accent/90 text-white rounded-lg flex items-center space-x-2"
        >
          <Upload className="w-4 h-4" />
          <span>Upload</span>
        </button>
      </div>

      {/* Breadcrumbs */}
      <div className="flex items-center px-4 py-2 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
        {breadcrumbs.map((crumb, index) => (
          <React.Fragment key={index}>
            {index > 0 && <ChevronRight className="w-4 h-4 text-gray-400 mx-2" />}
            <button
              onClick={() => navigateToBreadcrumb(index)}
              className={`flex items-center space-x-1 text-sm ${
                index === breadcrumbs.length - 1
                  ? 'text-gray-900 dark:text-white font-medium'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              {index === 0 && <Home className="w-4 h-4" />}
              <span>{crumb.name}</span>
            </button>
          </React.Fragment>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center space-x-3">
          {/* Search */}
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search assets..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm w-64"
            />
          </div>

          {/* Type filter */}
          <select
            value={selectedType || ''}
            onChange={(e) => setSelectedType(e.target.value || null)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
          >
            <option value="">All Types</option>
            <option value="document">Documents</option>
            <option value="image">Images</option>
            <option value="video">Videos</option>
            <option value="audio">Audio</option>
            <option value="web_link">Web Links</option>
          </select>

          {/* Tags dropdown */}
          {tags.length > 0 && (
            <div className="relative">
              <button className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm flex items-center space-x-2">
                <Tag className="w-4 h-4" />
                <span>Tags ({selectedTags.length})</span>
              </button>
            </div>
          )}
        </div>

        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {totalAssets} assets
          </span>
          <div className="flex items-center border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 ${viewMode === 'grid' ? 'bg-gray-100 dark:bg-gray-700' : ''}`}
            >
              <Grid className="w-4 h-4 text-gray-600 dark:text-gray-400" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 ${viewMode === 'list' ? 'bg-gray-100 dark:bg-gray-700' : ''}`}
            >
              <List className="w-4 h-4 text-gray-600 dark:text-gray-400" />
            </button>
          </div>
          <button onClick={fetchData} className="p-2 text-gray-400 hover:text-gray-600">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-4">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="w-8 h-8 text-gray-400 animate-spin" />
          </div>
        ) : (
          <>
            {/* Folders */}
            {folders.length > 0 && (
              <div className="mb-6">
                <h2 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Folders</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {folders.map(renderFolderCard)}
                </div>
              </div>
            )}

            {/* Assets */}
            {assets.length > 0 ? (
              viewMode === 'grid' ? (
                <div>
                  <h2 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Files</h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                    {assets.map(renderAssetCard)}
                  </div>
                </div>
              ) : (
                <div>
                  <h2 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Files</h2>
                  <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Name</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Type</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Size</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Info</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Created</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {assets.map(renderAssetRow)}
                      </tbody>
                    </table>
                  </div>
                </div>
              )
            ) : folders.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-gray-500 dark:text-gray-400">
                <Upload className="w-12 h-12 mb-4 opacity-50" />
                <p className="text-lg font-medium">No media files yet</p>
                <p className="text-sm">Upload your first file to get started</p>
                <button
                  onClick={() => setShowUploadModal(true)}
                  className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Upload File
                </button>
              </div>
            ) : null}
          </>
        )}
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <UploadModal
          folderId={currentFolder}
          onClose={() => setShowUploadModal(false)}
          onSuccess={() => {
            setShowUploadModal(false);
            fetchData();
          }}
        />
      )}

      {/* Folder Modal */}
      {showFolderModal && (
        <FolderModal
          parentFolderId={currentFolder}
          onClose={() => setShowFolderModal(false)}
          onSuccess={() => {
            setShowFolderModal(false);
            fetchData();
          }}
        />
      )}

      {/* Asset Detail Drawer */}
      {showAssetDetail && selectedAsset && (
        <AssetDetailDrawer
          asset={selectedAsset}
          onClose={() => {
            setShowAssetDetail(false);
            setSelectedAsset(null);
          }}
          onUpdate={fetchData}
        />
      )}

      {/* Share Modal */}
      {showShareModal && selectedAsset && (
        <ShareModal
          asset={selectedAsset}
          onClose={() => {
            setShowShareModal(false);
            setSelectedAsset(null);
          }}
        />
      )}
    </div>
  );
}

// Upload Modal Component
function UploadModal({
  folderId,
  onClose,
  onSuccess
}: {
  folderId: number | null;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [assetType, setAssetType] = useState('document');
  const [externalUrl, setExternalUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      if (!name) setName(selectedFile.name);

      // Auto-detect type
      const mime = selectedFile.type;
      if (mime.startsWith('image/')) setAssetType('image');
      else if (mime.startsWith('video/')) setAssetType('video');
      else if (mime.startsWith('audio/')) setAssetType('audio');
      else setAssetType('document');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setUploading(true);

    try {
      // Create asset
      const createRes = await fetch(`${getWeaveinApiUrl()}/api/media/assets`, {
        method: 'POST',
        headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          name,
          description,
          asset_type: assetType,
          folder_id: folderId,
          external_url: assetType === 'web_link' ? externalUrl : undefined
        })
      });

      if (!createRes.ok) throw new Error('Failed to create asset');
      const asset = await createRes.json();

      // Upload file if present
      if (file && assetType !== 'web_link') {
        const formData = new FormData();
        formData.append('file', file);

        const uploadRes = await fetch(
          `${getWeaveinApiUrl()}/api/media/assets/${asset.id}/upload`,
          {
            method: 'POST',
            headers: buildAuthHeaders(),
            body: formData
          }
        );

        if (!uploadRes.ok) throw new Error('Failed to upload file');
      }

      onSuccess();
    } catch (err) {
      console.error('Upload error:', err);
      alert('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Upload Media</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Asset Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Type
            </label>
            <select
              value={assetType}
              onChange={(e) => setAssetType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="document">Document</option>
              <option value="image">Image</option>
              <option value="video">Video</option>
              <option value="audio">Audio</option>
              <option value="web_link">Web Link</option>
            </select>
          </div>

          {/* File Upload or URL */}
          {assetType === 'web_link' ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                URL
              </label>
              <input
                type="url"
                value={externalUrl}
                onChange={(e) => setExternalUrl(e.target.value)}
                placeholder="https://example.com"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                required
              />
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                File
              </label>
              <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center">
                {file ? (
                  <div className="flex items-center justify-center space-x-2">
                    <File className="w-8 h-8 text-gray-400" />
                    <span className="text-gray-900 dark:text-white">{file.name}</span>
                    <button
                      type="button"
                      onClick={() => setFile(null)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <label className="cursor-pointer">
                    <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <span className="text-gray-500 dark:text-gray-400">Click to select or drag file here</span>
                    <input
                      type="file"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                  </label>
                )}
              </div>
            </div>
          )}

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Asset name"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description (optional)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          {/* Progress */}
          {uploading && (
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={uploading || (!file && assetType !== 'web_link')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {uploading ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span>Uploading...</span>
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  <span>Upload</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Folder Modal Component
function FolderModal({
  parentFolderId,
  onClose,
  onSuccess
}: {
  parentFolderId: number | null;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isShared, setIsShared] = useState(true);
  const [creating, setCreating] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);

    try {
      const res = await fetch(`${getWeaveinApiUrl()}/api/media/folders`, {
        method: 'POST',
        headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          name,
          description,
          parent_folder_id: parentFolderId,
          is_shared: isShared
        })
      });

      if (!res.ok) throw new Error('Failed to create folder');
      onSuccess();
    } catch (err) {
      console.error('Create folder error:', err);
      alert('Failed to create folder');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">New Folder</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Folder name"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description (optional)
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={isShared}
              onChange={(e) => setIsShared(e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              Share with organization users
            </span>
          </label>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={creating}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {creating ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Asset Detail Drawer Component
function AssetDetailDrawer({
  asset,
  onClose,
  onUpdate
}: {
  asset: MediaAsset;
  onClose: () => void;
  onUpdate: () => void;
}) {
  const { fmt } = useFormattedDate();
  const [loading, setLoading] = useState(true);
  const [fullAsset, setFullAsset] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<'details' | 'translations' | 'links' | 'analytics'>('details');
  const [showTranslationModal, setShowTranslationModal] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [analytics, setAnalytics] = useState<any>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  useEffect(() => {
    fetchAssetDetails();
  }, [asset.id]);

  useEffect(() => {
    if (activeTab === 'analytics') {
      fetchAnalytics();
    }
  }, [activeTab]);

  const fetchAssetDetails = async () => {
    try {
      const res = await fetch(`${getWeaveinApiUrl()}/api/media/assets/${asset.id}`, {
        headers: buildAuthHeaders()
      });
      if (res.ok) {
        setFullAsset(await res.json());
      }
    } catch (err) {
      console.error('Error fetching asset details:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    if (!fullAsset?.sharing_links?.length) return;
    setAnalyticsLoading(true);
    try {
      // Aggregate analytics from all links
      const allAnalytics: any[] = [];
      for (const link of fullAsset.sharing_links) {
        const res = await fetch(`${getWeaveinApiUrl()}/api/media/links/${link.id}/analytics`, {
          headers: buildAuthHeaders()
        });
        if (res.ok) {
          const data = await res.json();
          allAnalytics.push({ link, ...data });
        }
      }
      setAnalytics(allAnalytics);
    } catch (err) {
      console.error('Error fetching analytics:', err);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const handleAddTranslation = async (languageCode: string, name: string, description: string) => {
    try {
      const res = await fetch(`${getWeaveinApiUrl()}/api/media/assets/${asset.id}/translations`, {
        method: 'POST',
        headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          language_code: languageCode,
          name: name || undefined,
          description: description || undefined,
          is_ai_translated: false
        })
      });
      if (res.ok) {
        await fetchAssetDetails();
        setShowTranslationModal(false);
      }
    } catch (err) {
      console.error('Error adding translation:', err);
    }
  };

  const TypeIcon = ASSET_TYPE_ICONS[asset.asset_type] || File;

  return (
    <div className="fixed inset-y-0 right-0 w-96 bg-white dark:bg-gray-800 shadow-xl border-l border-gray-200 dark:border-gray-700 z-40 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center space-x-3">
          <div className={`p-2 rounded ${ASSET_TYPE_COLORS[asset.asset_type]}`}>
            <TypeIcon className="w-5 h-5" />
          </div>
          <div>
            <h2 className="font-semibold text-gray-900 dark:text-white truncate max-w-48">
              {asset.name}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">{asset.asset_type}</p>
          </div>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 dark:border-gray-700">
        {['details', 'translations', 'links', 'analytics'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab as any)}
            className={`flex-1 px-4 py-2 text-sm font-medium ${
              activeTab === tab
                ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'
            }`}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-4">
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <RefreshCw className="w-6 h-6 text-gray-400 animate-spin" />
          </div>
        ) : activeTab === 'details' ? (
          <div className="space-y-4">
            {asset.description && (
              <div>
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Description</h4>
                <p className="text-gray-900 dark:text-white">{asset.description}</p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Size</h4>
                <p className="text-gray-900 dark:text-white">{asset.file_size ? `${(asset.file_size / 1024 / 1024).toFixed(2)} MB` : '-'}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Created</h4>
                <p className="text-gray-900 dark:text-white">{fmt.short(asset.created_at)}</p>
              </div>
            </div>
            <div className="flex items-center space-x-4 pt-4">
              <label className="flex items-center space-x-2">
                <input type="checkbox" checked={asset.is_public} readOnly className="rounded" />
                <span className="text-sm text-gray-700 dark:text-gray-300">Public</span>
              </label>
              <label className="flex items-center space-x-2">
                <input type="checkbox" checked={asset.is_shared_org} readOnly className="rounded" />
                <span className="text-sm text-gray-700 dark:text-gray-300">Shared in Org</span>
              </label>
            </div>
            <ComplianceForm asset={asset} fullAsset={fullAsset} onSaved={() => { fetchAssetDetails(); onUpdate(); }} />
          </div>
        ) : activeTab === 'translations' ? (
          <div className="space-y-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {fullAsset?.translations?.length || 0} translations available
            </p>
            {fullAsset?.translations?.map((t: any) => (
              <div key={t.id} className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-gray-900 dark:text-white">{t.language_code}</span>
                  {t.is_ai_translated && (
                    <span className="text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 px-2 py-0.5 rounded">
                      AI Translated
                    </span>
                  )}
                </div>
                {t.name && <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{t.name}</p>}
              </div>
            ))}
            <button
              onClick={() => setShowTranslationModal(true)}
              className="w-full py-2 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-gray-500 dark:text-gray-400 hover:border-blue-500 hover:text-blue-500"
            >
              + Add Translation
            </button>
          </div>
        ) : activeTab === 'links' ? (
          <div className="space-y-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {fullAsset?.sharing_links?.length || 0} sharing links
            </p>
            {fullAsset?.sharing_links?.map((link: any) => (
              <div key={link.id} className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="flex items-center justify-between">
                  <code className="text-sm text-blue-600 dark:text-blue-400">{link.short_code}</code>
                  <button
                    onClick={() => navigator.clipboard.writeText(link.full_url || `${link.short_code}`)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">{link.link_type}</p>
              </div>
            ))}
            <button
              onClick={() => setShowLinkModal(true)}
              className="w-full py-2 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-gray-500 dark:text-gray-400 hover:border-blue-500 hover:text-blue-500"
            >
              + Create Link
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {analyticsLoading ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-6 h-6 text-gray-400 animate-spin" />
              </div>
            ) : !fullAsset?.sharing_links?.length ? (
              <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                <BarChart2 className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No links created yet</p>
                <button
                  onClick={() => setShowLinkModal(true)}
                  className="mt-2 text-blue-600 dark:text-blue-400 text-sm hover:underline"
                >
                  Create a link to track analytics
                </button>
              </div>
            ) : analytics ? (
              <div className="space-y-4">
                {analytics.map((item: any, idx: number) => (
                  <div key={idx} className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <code className="text-sm text-blue-600 dark:text-blue-400">{item.link.short_code}</code>
                      <span className="text-xs text-gray-500">{item.link.link_type}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Total Clicks:</span>
                        <span className="ml-1 font-medium text-gray-900 dark:text-white">{item.total_clicks || 0}</span>
                      </div>
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Unique:</span>
                        <span className="ml-1 font-medium text-gray-900 dark:text-white">{item.unique_visitors || 0}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                <BarChart2 className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No analytics data available</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex space-x-2">
        <button className="flex-1 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 flex items-center justify-center space-x-2">
          <Download className="w-4 h-4" />
          <span>Download</span>
        </button>
        <button
          onClick={() => setShowLinkModal(true)}
          className="flex-1 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center space-x-2"
        >
          <Share2 className="w-4 h-4" />
          <span>Share</span>
        </button>
      </div>

      {/* Translation Modal */}
      {showTranslationModal && (
        <TranslationModal
          onClose={() => setShowTranslationModal(false)}
          onSubmit={handleAddTranslation}
          existingLanguages={fullAsset?.translations?.map((t: any) => t.language_code) || []}
        />
      )}

      {/* Link Modal */}
      {showLinkModal && (
        <ShareModal
          asset={asset}
          onClose={() => {
            setShowLinkModal(false);
            fetchAssetDetails();
          }}
        />
      )}
    </div>
  );
}

// Translation Modal Component
function TranslationModal({
  onClose,
  onSubmit,
  existingLanguages
}: {
  onClose: () => void;
  onSubmit: (languageCode: string, name: string, description: string) => void;
  existingLanguages: string[];
}) {
  const [languageCode, setLanguageCode] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const LANGUAGES = [
    { code: 'en', name: 'English' }, { code: 'es', name: 'Spanish' },
    { code: 'fr', name: 'French' }, { code: 'de', name: 'German' },
    { code: 'it', name: 'Italian' }, { code: 'pt', name: 'Portuguese' },
    { code: 'zh', name: 'Chinese' }, { code: 'ja', name: 'Japanese' },
    { code: 'ko', name: 'Korean' }, { code: 'ar', name: 'Arabic' },
    { code: 'hi', name: 'Hindi' }, { code: 'ru', name: 'Russian' },
    { code: 'vi', name: 'Vietnamese' }, { code: 'tl', name: 'Tagalog' },
  ];

  const availableLanguages = LANGUAGES.filter(l => !existingLanguages.includes(l.code));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!languageCode) return;
    setSubmitting(true);
    await onSubmit(languageCode, name, description);
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Add Translation</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Language
            </label>
            <select
              value={languageCode}
              onChange={(e) => setLanguageCode(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">Select a language</option>
              {availableLanguages.map(l => (
                <option key={l.code} value={l.code}>{l.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Translated Name (optional)
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter translated name"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Translated Description (optional)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter translated description"
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
            />
          </div>
          <div className="flex justify-end space-x-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!languageCode || submitting}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {submitting ? 'Adding...' : 'Add Translation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Share Modal Component - Enhanced with Short URL generation and UTM tracking
function ShareModal({
  asset,
  onClose
}: {
  asset: MediaAsset;
  onClose: () => void;
}) {
  const [linkType, setLinkType] = useState('preview');
  const [expiresIn, setExpiresIn] = useState('never');
  const [createdLink, setCreatedLink] = useState<string | null>(null);
  const [shortCode, setShortCode] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [customSlug, setCustomSlug] = useState('');
  const [utmSource, setUtmSource] = useState('');
  const [utmMedium, setUtmMedium] = useState('');
  const [utmCampaign, setUtmCampaign] = useState('');
  const [maxClicks, setMaxClicks] = useState('');
  const [requiresAuth, setRequiresAuth] = useState(false);
  const [isPublic, setIsPublic] = useState(true);

  const handleCreateLink = async () => {
    setCreating(true);
    setError(null);
    try {
      let expiresAt = null;
      if (expiresIn !== 'never') {
        const days = parseInt(expiresIn);
        expiresAt = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString();
      }

      const res = await fetch(`${getWeaveinApiUrl()}/api/media/links`, {
        method: 'POST',
        headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          asset_id: asset.id,
          link_type: linkType,
          expires_at: expiresAt,
          custom_slug: customSlug || undefined,
          utm_source: utmSource || undefined,
          utm_medium: utmMedium || undefined,
          utm_campaign: utmCampaign || undefined,
          max_clicks: maxClicks ? parseInt(maxClicks) : undefined,
          requires_auth: requiresAuth,
          is_public: isPublic
        })
      });

      if (res.ok) {
        const link = await res.json();
        setCreatedLink(link.full_url);
        setShortCode(link.short_code);
      } else {
        const errorData = await res.json().catch(() => ({ detail: 'Unknown error' }));
        setError(errorData.detail || `Failed to create link (${res.status})`);
      }
    } catch (err) {
      console.error('Error creating link:', err);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setCreating(false);
    }
  };

  const copyToClipboard = () => {
    if (createdLink) {
      navigator.clipboard.writeText(createdLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Generate Short URL
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4 overflow-y-auto flex-1">
          {createdLink ? (
            <div className="space-y-4">
              <div className="flex items-center space-x-2 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <Check className="w-5 h-5 text-green-500" />
                <span className="text-green-700 dark:text-green-400">Short URL created successfully!</span>
              </div>

              {/* Short URL Display */}
              <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                    Short URL
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={createdLink}
                      readOnly
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white font-mono text-sm"
                    />
                    <button
                      onClick={copyToClipboard}
                      className={`p-2 rounded-lg transition-colors ${
                        copied
                          ? 'bg-green-600 text-white'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {shortCode && (
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                      Short Code
                    </label>
                    <code className="text-sm bg-gray-200 dark:bg-gray-600 px-2 py-1 rounded">
                      {shortCode}
                    </code>
                  </div>
                )}
              </div>

              {/* QR Code placeholder */}
              <div className="p-4 border border-gray-200 dark:border-gray-600 rounded-lg text-center">
                <div className="w-32 h-32 bg-gray-100 dark:bg-gray-700 mx-auto mb-2 rounded flex items-center justify-center">
                  <span className="text-gray-400 text-xs">QR Code</span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Scan to access the content
                </p>
              </div>

              <button
                onClick={() => {
                  setCreatedLink(null);
                  setShortCode(null);
                  setCustomSlug('');
                  setUtmSource('');
                  setUtmMedium('');
                  setUtmCampaign('');
                }}
                className="w-full py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Create Another Link
              </button>
            </div>
          ) : (
            <>
              {/* Asset Info */}
              <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <p className="text-sm font-medium text-gray-900 dark:text-white">{asset.name}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">{asset.asset_type}</p>
              </div>

              {/* Basic Options */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Link Type
                  </label>
                  <select
                    value={linkType}
                    onChange={(e) => setLinkType(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  >
                    <option value="preview">Preview</option>
                    <option value="direct_download">Direct Download</option>
                    <option value="redirect">Redirect</option>
                    <option value="embed">Embed</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Expires
                  </label>
                  <select
                    value={expiresIn}
                    onChange={(e) => setExpiresIn(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  >
                    <option value="never">Never</option>
                    <option value="1">1 day</option>
                    <option value="7">7 days</option>
                    <option value="30">30 days</option>
                    <option value="90">90 days</option>
                    <option value="365">1 year</option>
                  </select>
                </div>
              </div>

              {/* Custom Slug */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Custom Slug (optional)
                </label>
                <div className="flex items-center">
                  <span className="px-3 py-2 bg-gray-100 dark:bg-gray-600 border border-r-0 border-gray-300 dark:border-gray-600 rounded-l-lg text-gray-500 dark:text-gray-400 text-sm">
                    short-url/
                  </span>
                  <input
                    type="text"
                    value={customSlug}
                    onChange={(e) => setCustomSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                    placeholder="my-custom-link"
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-r-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  />
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Leave empty for auto-generated code
                </p>
              </div>

              {/* Access Options */}
              <div className="flex items-center space-x-4">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={isPublic}
                    onChange={(e) => setIsPublic(e.target.checked)}
                    className="rounded border-gray-300 dark:border-gray-600 text-blue-600"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Public Access</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={requiresAuth}
                    onChange={(e) => setRequiresAuth(e.target.checked)}
                    className="rounded border-gray-300 dark:border-gray-600 text-blue-600"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Require Login</span>
                </label>
              </div>

              {/* Advanced Options Toggle */}
              <button
                type="button"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="text-sm text-blue-600 dark:text-blue-400 hover:underline flex items-center space-x-1"
              >
                <span>{showAdvanced ? 'Hide' : 'Show'} UTM & Tracking Options</span>
                <ChevronRight className={`w-4 h-4 transition-transform ${showAdvanced ? 'rotate-90' : ''}`} />
              </button>

              {/* Advanced Options */}
              {showAdvanced && (
                <div className="space-y-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white">UTM Parameters</h4>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                        Source
                      </label>
                      <input
                        type="text"
                        value={utmSource}
                        onChange={(e) => setUtmSource(e.target.value)}
                        placeholder="facebook"
                        className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                        Medium
                      </label>
                      <input
                        type="text"
                        value={utmMedium}
                        onChange={(e) => setUtmMedium(e.target.value)}
                        placeholder="social"
                        className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                        Campaign
                      </label>
                      <input
                        type="text"
                        value={utmCampaign}
                        onChange={(e) => setUtmCampaign(e.target.value)}
                        placeholder="summer2024"
                        className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                      Max Clicks (optional)
                    </label>
                    <input
                      type="number"
                      value={maxClicks}
                      onChange={(e) => setMaxClicks(e.target.value)}
                      placeholder="Unlimited"
                      min="1"
                      className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                    />
                  </div>
                </div>
              )}

              {error && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                </div>
              )}

              <button
                onClick={handleCreateLink}
                disabled={creating}
                className="w-full py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center space-x-2 font-medium"
              >
                {creating ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <LinkIcon className="w-4 h-4" />
                )}
                <span>{creating ? 'Generating...' : 'Generate Short URL'}</span>
              </button>
            </>
          )}
        </div>

        <div className="p-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="w-full py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function ComplianceForm({
  asset,
  fullAsset,
  onSaved,
}: {
  asset: MediaAsset;
  fullAsset: any;
  onSaved: () => void;
}) {
  const { isRegulated } = useComplianceMode();
  const initial = fullAsset || asset;
  const toDateInput = (val?: string | null) => {
    if (!val) return '';
    return val.length >= 10 ? val.slice(0, 10) : val;
  };
  const [effectiveDate, setEffectiveDate] = useState<string>(toDateInput(initial?.effective_date));
  const [expirationDate, setExpirationDate] = useState<string>(toDateInput(initial?.expiration_date));
  const [expirationPolicy, setExpirationPolicy] = useState<string>(
    (initial?.expiration_policy as string) || 'never',
  );
  const [allowUseAfterExpiry, setAllowUseAfterExpiry] = useState<boolean>(
    Boolean(initial?.allow_use_after_expiry),
  );
  const [regulatedCategory, setRegulatedCategory] = useState<string>(
    (initial?.regulated_category as string) || '',
  );
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [submittingApproval, setSubmittingApproval] = useState(false);
  const [approvalSubmitted, setApprovalSubmitted] = useState(false);

  const submitForApproval = async () => {
    setSubmittingApproval(true);
    try {
      await approvalsAPI.submit({
        subject_kind: 'media_asset',
        subject_id: asset.id,
        subject_label: asset.name,
        summary: `Submitted from Media Library — ${asset.asset_type}${
          regulatedCategory ? ` · ${regulatedCategory}` : ''
        }`,
      });
      setApprovalSubmitted(true);
      onSaved();
    } catch (err: any) {
      alert(`Failed to submit for review: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setSubmittingApproval(false);
    }
  };

  const complianceStatus = (fullAsset?.compliance_status || asset.compliance_status) as string | undefined;
  const isPendingOrActive = !complianceStatus || complianceStatus === 'active' || complianceStatus === 'expiring_soon';

  useEffect(() => {
    const next = fullAsset || asset;
    setEffectiveDate(toDateInput(next?.effective_date));
    setExpirationDate(toDateInput(next?.expiration_date));
    setExpirationPolicy((next?.expiration_policy as string) || 'never');
    setAllowUseAfterExpiry(Boolean(next?.allow_use_after_expiry));
    setRegulatedCategory((next?.regulated_category as string) || '');
  }, [fullAsset, asset]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(
        `${getWeaveinApiUrl()}/api/media/assets/${asset.id}/compliance`,
        {
          method: 'PATCH',
          headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify({
            effective_date: effectiveDate || null,
            expiration_date: expirationDate || null,
            expiration_policy: expirationPolicy,
            allow_use_after_expiry: allowUseAfterExpiry,
            regulated_category: regulatedCategory || null,
          }),
        },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to update compliance');
      }
      setSavedAt(new Date().toISOString());
      onSaved();
    } catch (err: any) {
      alert(`Error: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
      <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3">Expiration</h4>
      <div className="space-y-3">
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
            Effective Date
          </label>
          <input
            type="date"
            value={effectiveDate}
            onChange={(e) => setEffectiveDate(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
            Expiration Date
          </label>
          <input
            type="date"
            value={expirationDate}
            onChange={(e) => setExpirationDate(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
          />
        </div>
        <RegulatedOnly>
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
              Expiration Policy
            </label>
            <select
              value={expirationPolicy}
              onChange={(e) => setExpirationPolicy(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            >
              <option value="never">Never expires</option>
              <option value="always">Always expires</option>
              <option value="sometimes">Sometimes expires</option>
            </select>
          </div>
          <label className="flex items-center justify-between">
            <span className="text-sm text-gray-700 dark:text-gray-300">
              Allow use after expiry
            </span>
            <input
              type="checkbox"
              checked={allowUseAfterExpiry}
              onChange={(e) => setAllowUseAfterExpiry(e.target.checked)}
              className="w-5 h-5"
            />
          </label>
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
              Regulated Category
            </label>
            <input
              type="text"
              value={regulatedCategory}
              onChange={(e) => setRegulatedCategory(e.target.value)}
              placeholder="e.g., consent_form, privacy_notice"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            />
          </div>
        </RegulatedOnly>
        <div className="flex items-center justify-between pt-2 gap-2">
          {savedAt && (
            <span className="text-xs text-green-600">Saved.</span>
          )}
          <div className="ml-auto flex items-center gap-2">
            {isRegulated && isPendingOrActive && (
              <button
                type="button"
                onClick={submitForApproval}
                disabled={submittingApproval || approvalSubmitted}
                className="px-4 py-2 bg-amber-600 text-white text-sm rounded-lg hover:bg-amber-700 disabled:opacity-50"
                title="Send this asset to the compliance approval queue"
              >
                {approvalSubmitted
                  ? 'Submitted'
                  : submittingApproval
                  ? 'Submitting…'
                  : 'Submit for review'}
              </button>
            )}
            {isRegulated && complianceStatus === 'pending_approval' && (
              <a
                href={`/admin/approvals?subject_kind=media_asset&subject_id=${asset.id}`}
                className="px-4 py-2 bg-amber-50 text-amber-800 text-sm rounded-lg hover:bg-amber-100"
              >
                View approval
              </a>
            )}
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save Compliance'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MediaLibrary;
